import {
  CheckboxGroup,
  CheckboxGroupCreateArgs,
  CheckboxGroupResultData,
  CheckboxGroupTranslation,
  CheckboxGroupTranslationCreate,
  CheckboxGroupUpdateArgs,
} from "types";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cacheKeyForDateRange, defaultChartQueryOptions } from "utils";

import { CheckboxGroupsAPI } from "api";

type UseCheckboxGroupResultsArgs = Parameters<typeof CheckboxGroupsAPI.getResults>[0];

const queryKeys = {
  all: () => ["checkbox-groups"] as const,
  detail: (id: number) => [...queryKeys.all(), id] as const,
  translations: (id: number) => [...queryKeys.detail(id), "translations"] as const,
  results: ({ startDate, endDate, ...params }: UseCheckboxGroupResultsArgs) => [
    ...queryKeys.all(),
    "result",
    {
      checkboxGroupId: params.checkboxGroupId,
      segmentUUID: params.segmentUUID,
      targetId: params.targetId,
      networkId: params.network,
      weightingSchemeName: params.weightingSchemeName,
      isRebaseOptOut: params.isRebaseOptOut,
      ...cacheKeyForDateRange({ startDate, endDate }),
    },
  ],
};

/**
 * Fetches a CheckboxGroup by ID.
 */
export const useCheckboxGroup = <T = CheckboxGroup>(
  id: number,
  options: UseQueryOptions<CheckboxGroup, unknown, T> = {},
) => useQuery<CheckboxGroup, unknown, T>(queryKeys.detail(id), () => CheckboxGroupsAPI.get(id), options);

/**
 * Fetches the results for a CheckboxGroup given an ID and filter options.
 */
export const useCheckboxGroupResults = <T = CheckboxGroupResultData>(
  params: UseCheckboxGroupResultsArgs,
  options: UseQueryOptions<CheckboxGroupResultData, unknown, T> = {},
) =>
  useQuery<CheckboxGroupResultData, unknown, T>(queryKeys.results(params), () => CheckboxGroupsAPI.getResults(params), {
    ...defaultChartQueryOptions,
    ...options,
  });

/**
 * Mutation to create a new CheckboxGroup.
 */
export const useCheckboxGroupCreate = (
  options: UseMutationOptions<CheckboxGroup, unknown, CheckboxGroupCreateArgs> = {},
) => {
  return useMutation((data) => CheckboxGroupsAPI.create(data), options);
};

/**
 * Mutation to update an existing CheckboxGroup.
 */
export const useCheckboxGroupUpdate = (
  id: number,
  options: UseMutationOptions<CheckboxGroup, unknown, CheckboxGroupUpdateArgs> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((data) => CheckboxGroupsAPI.update(id, data), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.detail(id));
      options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Fetches the list of translations for a CheckboxGroup.
 */
export const useCheckboxGroupTranslations = <T = CheckboxGroupTranslation[]>(
  id: number,
  options: UseQueryOptions<CheckboxGroupTranslation[], unknown, T> = {},
) =>
  useQuery<CheckboxGroupTranslation[], unknown, T>(
    queryKeys.translations(id),
    () => CheckboxGroupsAPI.listTranslations(id),
    options,
  );

/**
 * Mutation to create a translation for a CheckboxGroup.
 */
export const useCheckboxGroupTranslationCreate = (
  originalGroupId: number,
  options: UseMutationOptions<CheckboxGroup, unknown, CheckboxGroupTranslationCreate> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((data) => CheckboxGroupsAPI.translate(data), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.translations(originalGroupId));
      options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to delete an existing CheckboxGroup.
 */
export const useCheckboxGroupDelete = (id: number, options: UseMutationOptions<void, unknown, number> = {}) => {
  return useMutation(() => CheckboxGroupsAPI.delete(id), options);
};

type FavoriteCheckboxQuestionToggleArgs = {
  id: string | number;
  /**
   * True to mark as favorite, false to remove from favorites.
   */
  isFavorite: boolean;
};

/**
 * Mutation to toggle the favorite status of a checkbox question.
 */
export const useCheckboxQuestionFavoriteToggle = (
  options: UseMutationOptions<boolean, unknown, FavoriteCheckboxQuestionToggleArgs> = {},
) => {
  return useMutation(async ({ id, isFavorite }: FavoriteCheckboxQuestionToggleArgs) => {
    isFavorite ? CheckboxGroupsAPI.favorite(id) : CheckboxGroupsAPI.unfavorite(id);
    return isFavorite;
  }, options);
};
